import { transformPropsBeforeRender } from '@dnb/link';
import { withPrefix } from 'gatsby';

declare global {
  /**
   * "__BASE_PATH__" Is set by Webpack DefinePlugin
   */
  const __BASE_PATH__: string;
}

export const initialLoad = () => {
  if (
    process.env.NODE_ENV === 'development' &&
    globalThis.IS_SPA_BUILD &&
    !globalThis.BUILD_EXISTING_SPA
  ) {
    globalThis.isTransformedSpaBuild = true;
  }

  /**
   * The above code is only used during the initial visit.
   * It grabs the hash path (#/page-path),
   * and sets globalThis.pagePath
   *
   * Gatsby is using globalThis.pagePath to load the first page.
   *
   */
  if (globalThis.isTransformedSpaBuild) {
    const hashPath = getHashPath();

    // Set pagePath which defines the initial page load, normally located in every HTML file
    globalThis.pagePath = hashPath;
  }

  /**
   * Because links are visible and copyable,
   * we want to have all our defined paths inside links to start with /#
   */
  if (globalThis.isTransformedSpaBuild) {
    globalThis.__locationHash = null;
    transformPropsBeforeRender(({ to }) => {
      if (!/^(http|#|\/#)/.test(to)) {
        to = '/#' + ((to.startsWith('/') ? '' : '/') + to);
      }
      return { to };
    });
  }
};

export const wrapPageElement = ({ element }) => {
  /**
   * This is only needed during navigate() within onPreRouteUpdate()
   */
  if (globalThis.__bypassRender) {
    return null;
  }

  return element;
};

export const onPreRouteUpdate = ({ prevLocation }) => {
  if (globalThis.isTransformedSpaBuild && prevLocation) {
    /**
     * 1. Use for browser back and forth navigation (listener)
     */
    const makeNavigation = shouldMakeNavigation(prevLocation);

    /**
     * 2. Use for click on links
     */
    const makeReplaceLocation = shouldReplaceLocation(prevLocation);

    // if (prevLocation?.hash?.startsWith('#')) {
    if (makeReplaceLocation) {
      globalThis.__bypassRender = false;

      const path = getPath(prevLocation);

      // Replace the href with our hashed path
      replaceLocationWithHashBasedPath(path);
    } else if (makeNavigation) {
      /**
       * Because the current page will render again when we call navigate() from within onPreRouteUpdate()
       * we set globalThis.__bypassRender to true, and right after the next onPreRouteUpdate, we set it to false again
       */
      globalThis.__bypassRender = true;

      globalThis.___replace(
        construtUrl([__BASE_PATH__, getHashPath(prevLocation, 'hash')])
      );
    }
  }
};

export const onRouteUpdate = ({ location }) => {
  if (globalThis.isTransformedSpaBuild) {
    const path = getPath(location);

    /**
     * Sometimes, browsers (FF) to not scroll to the hash,
     * when the path is like this: /#/app-1/page-b/#id
     * so we do help out to scroll to the hash, if it exists
     */
    handleSafeHashId(path);

    globalThis.__bypassRender = false;
  }
};

/**
 * Once a globalThis.pagePath is set (from within this file)
 * We replace the href location with this hashed pathname during first visit.
 * This also does the job on a hard refresh.
 *
 * We could use onClientEntry, but its too early,
 * so we use onInitialClientRender.
 */
export const onInitialClientRender = () => {
  if (globalThis.isTransformedSpaBuild) {
    /**
     * Replace the original ___navigate to remove a starting hash
     * Gatsby's loadPage does not need this.
     */
    if (!globalThis.___navigateOrig) {
      globalThis.___navigateOrig = globalThis.___navigate;
      globalThis.___navigate = (to, options) => {
        const startsWithHash = to.startsWith('#') || to.startsWith('/#');
        if (startsWithHash) {
          to = to.substring(to.indexOf('#') + 1);
        }

        globalThis.___navigateOrig(to, options);
      };
    }

    if (globalThis.pagePath) {
      if (
        process.env.NODE_ENV === 'development' &&
        shouldMakeNavigation(globalThis.location)
      ) {
        globalThis.___replace(globalThis.pagePath);
      } else {
        replaceLocationWithHashBasedPath(globalThis.pagePath);
        handleSafeHashId(globalThis.pagePath);
      }
    }
  }
};

/**
 * Check if the location "hash" is valid, but the "pathname" not
 *
 * @param {object} location Navigator location
 * @returns boolean
 */
function shouldMakeNavigation(location: Location) {
  return location?.hash?.startsWith('#');
}

/**
 * Check if the location "pathname" is a valid path
 *
 * @param {object} location Navigator location
 * @returns boolean
 */
function shouldReplaceLocation(location: Location) {
  const pathname = (location?.pathname || '').replace(__BASE_PATH__, '');
  return pathname.length > 1;
}

/**
 * Get a path from navigator location
 *
 * @param {object} location Navigator location
 * @returns string path location
 */
function getHashPath(location = globalThis.location, use = 'href') {
  const path = location[use];
  const hashIndex = path.indexOf('#');

  return (
    (hashIndex === -1 ? '' : path.substring(hashIndex + 1)) +
    (location.search || '')
  );
}

/**
 * Get a basic constructed path from navigator location
 *
 * @param {object} location Navigator location
 * @returns string path location
 */
function getPath(location = globalThis.location) {
  return location.pathname + (location.search || '') + (location.hash || '');
}

/**
 * Helper function to deal with a hash IDs
 *
 * @param {string} path Navigator location path
 * @returns void
 */
function handleSafeHashId(path: string) {
  const startsWithHash = path.startsWith('#') || path.startsWith('/#');

  if (!startsWithHash) {
    const safeHashIndex = path.indexOf('#');
    globalThis.__locationHash = null;

    if (safeHashIndex > 0) {
      const hashId = path.substring(safeHashIndex + 1);
      if (hashId) {
        globalThis.__locationHash = hashId;
        clearTimeout(globalThis.__hashIdTimeout);
        globalThis.__hashIdTimeout = setTimeout(() => {
          scrollToHashSafeHashId(hashId);
        }, 500);
      }
    }
  }
}

/**
 * Helper function to deal with a hash IDs
 *
 * Check if the hash is an existing ID,
 * then it means we want to scroll there
 *
 * @param {string} path Navigator location hash
 * @returns void
 */
function scrollToHashSafeHashId(hashId: string) {
  try {
    const element =
      document.getElementById(hashId) ||
      document.querySelector(`a[href="#${hashId}"]`);

    if (element) {
      element.scrollIntoView();
    }
  } catch (e) {
    //
  }
}

/**
 * Replace location with hash based path
 *
 * @param {string} path Navigator location href
 * @returns void
 */
function replaceLocationWithHashBasedPath(path?: string) {
  if (path) {
    // If a "pathPrefix" is set, remove it from being hashed
    path = path.replace(__BASE_PATH__, '');

    const startsWithHash = path.startsWith('#') || path.startsWith('/#');
    const pathname = (startsWithHash ? '' : '/#') + path;

    globalThis.history.replaceState({}, '', withPrefix(pathname));
  }
}

/**
 * Create a valid url form given paths
 *
 * @param {array} paths List of string paths
 * @returns {string} valid url
 */
function construtUrl(paths: Array<string>): string {
  return (
    '/' +
    paths
      .filter(Boolean)
      .map((path) => String(path).replace(/^\/|\/$/g, ''))
      .join('/')
  );
}
